/**
 * Internationalization configuration for the StartUs Admin Panel.
 *
 * This module sets up react-i18next for the admin application, combining
 * common translations from the shared library with admin-specific translations.
 * The configuration supports English and Spanish languages with automatic
 * language detection based on browser settings. Namespaces are used to
 * organize translations by feature area (common vs admin-specific).
 *
 * Translation structure:
 * - common: Shared translations used across the entire application
 * - admin: Admin panel specific translations for dashboards, modals, etc.
 *
 * New translations should be added to the respective JSON files in locales/
 * and accessed in components using the useTranslation hook.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { commonResources } from '@startus/lib';

import enAdmin from './locales/en/admin.json';
import esAdmin from './locales/es/admin.json';
import ptBRAdmin from './locales/pt-BR/admin.json';

/**
 * Combined translation resources for all supported languages.
 * Merges common translations from the shared library with admin-specific ones.
 */
const resources = {
  en: {
    ...commonResources.en,
    admin: enAdmin,
  },
  es: {
    ...commonResources.es,
    admin: esAdmin,
  },
  'pt-BR': {
    ...commonResources['pt-BR'],
    admin: ptBRAdmin,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'admin'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  });

export default i18n;
